import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { handleGetRequest, handlePatchRequest } from '../../utils/api-handler.util'
import { formatTime } from '../../utils/date.util'
import StatusPill from '../../components/Verification/StatusPill'
import VerificationChecklist from '../../components/Verification/VerificationCheckList'
import ApproveRejectActions from '../../components/Verification/ApproveRejectButtons'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import VerificationStepper from '../../components/Verification/VerificationStepper'
import { showToaster } from '../../utils/show-toaster.util'
import { VERIFICATION_STEPS } from '../../constants/verificationSteps'
import { VERIFICATION_STATUSES } from '../../constants/verificationStatuses'
import StartVerificationButton from '../../components/Verification/StartVerificationButton'
import { useState } from 'react'
import Modal from '../../components/common/Modal'

export default function ShopVerificationDetails() {
  const { id } = useParams()
  const [showReopenModal, setShowReopenModal] = useState(false)

  const { data: verificationData, isLoading } = useQuery({
    queryKey: QueryKeys.adminShopVerificationDetails(id),
    queryFn: () => handleGetRequest(`/admin/shop-verification/${id}`),
  })

  const verification = verificationData?.data

  const startVerificationMutation = useMutation({
    mutationFn: () => handlePatchRequest(`admin/shop-verification/${id}/start-review`),
    onSuccess: () => {
      QueryKeys.adminShopVerificationDetails.invalidate(id)
      QueryKeys.adminListShopVerifications.invalidateAll()
      showToaster('success', 'Verification process started')
    },
    onError: () => {
      showToaster('error', 'Failed to start verification')
    },
  })

  const hasVistedShopMutation = useMutation({
    mutationFn: (hasVisitedShop) =>
      handlePatchRequest(`/admin/shop-verification/${id}/visited-shop`, {
        hasVisitedShop,
      }),
    onSuccess: () => {
      QueryKeys.adminShopVerificationDetails.invalidate(id)
      showToaster('success', 'Shop Visitation Updated Successfully')
    },
  })

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-40">
        <LoadingSpinner />
      </div>
    )
  }

  const hasValidShopDetails =
    verification?.shopName &&
    verification?.buildingName &&
    verification?.floor &&
    verification?.shopNumber

  const getCurrentStepperKey = () => {
    if (!hasValidShopDetails) return VERIFICATION_STEPS.SHOP_INFO
    if (!verification.hasAllDocsApproved) return VERIFICATION_STEPS.DOCUMENTS
    if (!verification.visitedShop) return VERIFICATION_STEPS.LOCATION
    return VERIFICATION_STEPS.FINAL_DECISION
  }

  const madeFinalDecision =
    verification?.status === VERIFICATION_STATUSES.approved.value ||
    verification?.status === VERIFICATION_STATUSES.rejected.value

  const handleReopenVerification = async () => {
    await startVerificationMutation.mutateAsync()
  }

  return (
    <div className="p-6 space-y-10 max-w-6xl mx-auto">
      <VerificationStepper
        currentStepKey={getCurrentStepperKey()}
        madeFinalDecision={madeFinalDecision}
      />

      {verification.status === VERIFICATION_STATUSES.submitted.value && (
        <StartVerificationButton
          onClick={() => startVerificationMutation.mutate()}
          isLoading={startVerificationMutation.isLoading}
        />
      )}

      <div className="bg-background shadow-sm border border-gray-100 rounded-lg p-4  gap-2">
        <span className="text-lg font-semibold">Shop Verification Details</span>
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700">Shop name:</span>
          <div className=" text-gray-900 flex items-center gap-3">
            {verification?.shopName || 'Not Provided'}

            <StatusPill status={verification.status} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700">Created At:</span>
          <div className="mt-1 text-gray-900">{formatTime(verification?.createdAt)}</div>
        </div>

        {verification?.assignedTo && (
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-700">Assigned To:</span>
            <div className=" text-gray-900">{verification?.assignedTo?.name || 'Not Assigned'}</div>
          </div>
        )}
        {verification.rejectionReason && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-700 text-sm">
              <strong>Rejection Reason:</strong> {verification.rejectionReason}
            </p>
          </div>
        )}
      </div>
      {verification.status !== VERIFICATION_STATUSES.submitted.value &&
        verification.status !== VERIFICATION_STATUSES.draft.value && (
          <>
            <VerificationChecklist
              verification={verification}
              shopDocuments={verification?.shopDocuments}
              hasVisitedShop={hasVistedShopMutation}
              hasValidShopDetails={hasValidShopDetails}
            />

            {madeFinalDecision ? (
              <div className="p-4 border rounded bg-neutral-200-50">
                <p className="mb-2">
                  <span className="font-medium">Status:</span>{' '}
                  {verification.status === VERIFICATION_STATUSES.approved.value ? (
                    <span className="text-green-600 font-semibold">Approved</span>
                  ) : (
                    <span className="text-red-600 font-semibold">Rejected</span>
                  )}
                </p>

                {verification.status === VERIFICATION_STATUSES.rejected.value && (
                  <p className="mb-2">
                    <span className="font-medium">Reason:</span>{' '}
                    <span>{verification.rejectionReason || 'Not provided'}</span>
                  </p>
                )}

                <button
                  onClick={() => setShowReopenModal(true)}
                  className="mt-3 px-4 py-2 text-sm bg-secondary-600 text-white rounded"
                >
                  Reopen Verification
                </button>
              </div>
            ) : (
              <ApproveRejectActions shop={verification} id={id} />
            )}

            {showReopenModal && (
              <Modal isOpen={true} onClose={() => setShowReopenModal(false)}>
                <h2 className="text-lg font-semibold mb-4">Reopen Verification</h2>
                <p className="mb-4 text-gray-700">
                  Are you sure you want to reopen this verification? The status will be reset to{' '}
                  <strong>In Review</strong>.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    className="px-4 py-2 rounded bg-neutral-200 hover:bg-neutral-200"
                    onClick={() => setShowReopenModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded bg-red-600 text-white hover:bg-red-700"
                    onClick={async () => {
                      await handleReopenVerification()
                      setShowReopenModal(false)
                    }}
                  >
                    Yes, Reopen
                  </button>
                </div>
              </Modal>
            )}
          </>
        )}
    </div>
  )
}
